/* eslint-disable react/prop-types */ // TS generic component — prop-types can't be inferred (false positive)
import React, { useState } from 'react';
import { Box, useColorModeValue } from '@chakra-ui/react';
import { FaArrowsAltV } from 'react-icons/fa';

// item 633/699 — a generic drag-reorder list for the sidebar (projects inside a customer group AND
// the TOOLS list). Order persists per-device in localStorage (keyed by `storageKey`). Uses the
// armed-grip pattern (item 604): the row's native `draggable` is OFF until you press the grip, so a
// plain click ALWAYS navigates (never hijacked). The grip is the up/down arrow (FaArrowsAltV) used
// everywhere else we reorder. `isPinned` (optional) keeps favorites at the top (projects only).
const readOrder = (key: string): string[] => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
};
const writeOrder = (key: string, ids: string[]): void => {
  try { window.localStorage.setItem(key, JSON.stringify(ids)); } catch { /* ignore quota/parse */ }
};

interface SortableListProps<T> {
  storageKey: string;
  items: T[];
  getId: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  isPinned?: (item: T) => boolean;
}

function SortableList<T>({
  storageKey, items, getId, renderItem, isPinned,
}: SortableListProps<T>): React.ReactElement {
  const [, force] = useState(0);
  const [armedId, setArmedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const gripColor = useColorModeValue('blackAlpha.500', 'whiteAlpha.500'); // item 958 — light-mode contrast (grip vanished on the light sidebar)

  // display order: pinned first (if any), then the manual (persisted) order, then natural order.
  const manual = readOrder(storageKey);
  const rank = (item: T): number => {
    const i = manual.indexOf(getId(item));
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const ordered = [...items].sort((a, b) => {
    const fa = Number(isPinned ? isPinned(a) : false);
    const fb = Number(isPinned ? isPinned(b) : false);
    if (fa !== fb) return fb - fa; // pinned/favorites to the top
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb; // then the manual order
    return 0; // else stable / natural
  });

  const drop = (targetId: string): void => {
    if (!draggingId || draggingId === targetId) return;
    const ids = ordered.map((item) => getId(item));
    const from = ids.indexOf(draggingId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    writeOrder(storageKey, ids);
    force((n) => n + 1);
  };

  return (
    <>
      {ordered.map((item) => {
        const id = getId(item);
        const isOver = overId === id && draggingId && draggingId !== id;
        return (
          <Box
            key={id}
            role="group"
            position="relative"
            draggable={armedId === id}
            onDragStart={() => setDraggingId(id)}
            onDragOver={(e) => { if (draggingId && draggingId !== id) { e.preventDefault(); setOverId(id); } }}
            onDrop={(e) => { e.preventDefault(); drop(id); setDraggingId(null); setOverId(null); setArmedId(null); }}
            onDragEnd={() => { setDraggingId(null); setOverId(null); setArmedId(null); }}
            opacity={draggingId === id ? 0.4 : 1}
            borderTop={isOver ? '2px solid' : '2px solid transparent'}
            borderTopColor={isOver ? 'brandGreen.300' : 'transparent'}
          >
            {/* the grip: quiet until row hover; pressing it ARMS native drag for this row only */}
            <Box
              as={FaArrowsAltV}
              position="absolute"
              left="-2px"
              top="50%"
              transform="translateY(-50%)"
              fontSize="12px"
              color={gripColor}
              cursor="grab"
              opacity={0}
              zIndex={2}
              _groupHover={{ opacity: 0.9 }}
              onMouseDown={() => setArmedId(id)}
              onMouseUp={() => setArmedId((cur) => (cur === id ? null : cur))}
              aria-label="Drag to reorder"
            />
            {renderItem(item)}
          </Box>
        );
      })}
    </>
  );
}

SortableList.defaultProps = { isPinned: undefined };

export default SortableList;
